import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Space, Table, Tag, Tooltip, Typography } from 'antd';
import {
    ConfigCorporateActionStatus,
    CORPORATE_ACTION_TYPE_LABELS,
    CorporateActionApplication,
    getConfigCorporateActions,
    NotOwedCorporateAction,
    PendingCorporateAction,
} from '../../services/corporateActionApi';
import { formatDecimal, formatInt } from '../../utils/numberFormat';

const { Text } = Typography;

/**
 * Corporate actions, from the owner's side: READ ONLY.
 *
 * There is no Apply here, and that is the design rather than a missing feature. An ex-rights
 * date is a fact about the market — the same fact for every account holding the symbol — and a
 * grid still priced in the pre-event unit is wrong, not differently configured: with
 * `allow_to_exceed_floor_ceiling` on (the default) its buy orders get CLAMPED to the ceiling.
 * So adjusting is platform work, done once by an admin for everybody, and the start gate holds
 * the bot down until it is done. Offering the owner a button implied a decision they do not
 * have. (The futures roll is the opposite: which contract to hold, and when, IS their call.)
 *
 * What this screen still has to get across, because none of it is guessable:
 *
 *  1. The bot will not start, and the reason is a pending adjustment — not their config, and
 *     not something they can clear themselves. It says who is on the hook instead.
 *  2. The numbers on screen were rewritten by someone else. The user did not type
 *     `grid_origin = 23.8`. (Precedent: OCC renames an adjusted option contract, TSLA → TSLA1,
 *     so nobody mistakes it for the standard one.)
 *  3. Whether the bot has already folded an adjustment into its own book — that is the line
 *     past which even an admin cannot revert, only correct.
 */

/**
 * Ex-dates in play, for the heading. Normally one — several events sharing an ex-date fold into
 * a single factor and a single apply — but two different dates can be owed at once, so this
 * lists what it finds rather than picking one.
 */
function pendingDates(rows: PendingCorporateAction[]): string {
    return Array.from(new Set(rows.map((p) => p.exDate))).sort().join(', ');
}

/**
 * What the event pays or issues, in the unit it was entered in.
 *
 * Kept to one short line: the event name in the left column already says WHAT is being issued,
 * so repeating "new shares" only wrapped the row and left the two rows different heights.
 */
function describeTerms(p: PendingCorporateAction): string {
    switch (p.type) {
        case 'CASH_DIVIDEND':
            return p.cashDividend != null ? `${formatInt(p.cashDividend)} VND / share` : '—';
        case 'STOCK_DIVIDEND':
        case 'BONUS_ISSUE':
            return p.ratioNew != null ? `${formatDecimal(p.ratioNew, 2)} per share held` : '—';
        case 'RIGHTS_ISSUE':
            return p.ratioNew != null
                ? `${formatDecimal(p.ratioNew, 2)} per share held at ${formatInt(p.subscriptionPrice ?? 0)} VND`
                : '—';
        case 'STOCK_SPLIT':
            return p.splitFactor != null ? `${formatDecimal(p.splitFactor, 2)} for every 1 held` : '—';
        default:
            return '—';
    }
}

interface Props {
    dataConfigId?: string | null;
}

export const CorporateActionPanel: React.FC<Props> = ({ dataConfigId }) => {
    const [state, setState] = useState<ConfigCorporateActionStatus | null>(null);

    const load = useCallback(async () => {
        if (!dataConfigId) {
            setState(null);
            return;
        }
        try {
            setState(await getConfigCorporateActions(dataConfigId));
        } catch {
            // Nothing to show is the honest fallback: this panel is an explanation, not a
            // control the page depends on.
            setState(null);
        }
    }, [dataConfigId]);

    useEffect(() => {
        void load();
    }, [load]);

    if (!dataConfigId || !state) return null;
    const { pending, applied } = state;
    const notOwed = state.notOwed ?? [];
    const live = applied.filter((a) => a.status === 'APPLIED');

    return (
        <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 12 }}>
            {pending.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message={`Corporate action (${pendingDates(pending)})`}
                    description={
                        // Table ruling, no table surface. antd's `Table` was rejected here for
                        // the surface rather than the shape: its own background, padding and
                        // radius read as a second card dropped inside the alert. A grid gives
                        // the ruled rows and aligned columns while inheriting the alert's
                        // background, so the two blocks in this panel stay one visual system.
                        //
                        // No `columnGap` on purpose — a gap would cut the row rule in half.
                        // The first cell pads to the right instead, so each rule runs unbroken
                        // across both columns the way a table's does.
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'max-content 1fr',
                                fontSize: 12,
                            }}
                        >
                            {pending.map((p: PendingCorporateAction, i: number) => {
                                const ruled = i < pending.length - 1;
                                const cell: React.CSSProperties = {
                                    padding: '5px 0',
                                    borderBottom: ruled ? '1px solid rgba(255, 255, 255, 0.10)' : undefined,
                                };
                                return (
                                    <React.Fragment key={`${p.symbol}:${p.exDate}:${p.type}:${i}`}>
                                        <div style={{ ...cell, paddingRight: 20 }}>
                                            <Text type={p.status === 'BLOCKED' ? 'danger' : 'secondary'}>
                                                {CORPORATE_ACTION_TYPE_LABELS[p.type] ?? 'corporate action'}
                                            </Text>
                                        </div>
                                        <div style={cell}>
                                            <Text>
                                                {describeTerms(p)}
                                                {p.reason && <Text type="danger"> · {p.reason}</Text>}
                                            </Text>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    }
                />
            )}

            {notOwed.length > 0 && (
                <Alert
                    type="info"
                    showIcon
                    message="A past corporate action was not applied — this wallet was created after the ex-date"
                    description={
                        <Space direction="vertical" size={6} style={{ width: '100%' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                The default assumption is that the price you typed is already
                                the <Text strong>post-adjustment</Text> price — adjusting again
                                would be a second adjustment for one event. If you typed a{' '}
                                <Text strong>pre-ex-date</Text> price, ask operations to adjust
                                this config on its own.
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                In that case the platform rewrites only the{' '}
                                <Text strong>config numbers</Text>: shares bought after the ex-date
                                were never entitled to the event, so quantity and cash dividend
                                stay <Text strong>unchanged</Text>.
                            </Text>
                            {notOwed.map((n: NotOwedCorporateAction) => (
                                <Space key={`${n.symbol}:${n.exDate}`} wrap>
                                    <Text strong>{n.symbol}</Text>
                                    <Text type="secondary">ex-date {n.exDate}</Text>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        wallet created {n.walletCreatedAt?.slice(0, 10) ?? '—'}
                                    </Text>
                                </Space>
                            ))}
                        </Space>
                    }
                />
            )}

            {live.length > 0 && (
                <Table<CorporateActionApplication>
                    size="small"
                    rowKey="id"
                    dataSource={live}
                    pagination={false}
                    columns={[
                        { title: 'Symbol', dataIndex: 'symbol', width: 80 },
                        { title: 'Ex-date', dataIndex: 'exDate', width: 110 },
                        {
                            title: 'Factors',
                            render: (_, a) => (
                                <Text code style={{ fontSize: 11 }}>
                                    price ×{formatDecimal(a.priceFactor, 4)} · shares ×
                                    {formatDecimal(a.shareFactor, 2)}
                                </Text>
                            ),
                        },
                        {
                            title: 'Status',
                            width: 190,
                            // The bot-folded line is the one an operator needs when something is
                            // wrong: past it, the position count is already × s and the broker
                            // really did credit the shares, so a revert would disagree with the
                            // account — only a corrected factor composes onto it.
                            render: (_, a) =>
                                a.foldedByBot ? (
                                    <Tooltip title="The bot has folded the factors into its own book (position count and dividend). An error past this point can only be fixed with a corrected factor, never reverted.">
                                        <Tag color="green">folded by bot</Tag>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Config adjusted, not folded into the bot's book yet. Operations can still revert it cleanly if the numbers are wrong.">
                                        <Tag color="blue">config written</Tag>
                                    </Tooltip>
                                ),
                        },
                    ]}
                />
            )}
        </Space>
    );
};

/**
 * The per-symbol status chip that used to be an Apply button.
 *
 * Kept in the Price Levels header — right next to the numbers an adjustment rewrites — because
 * that is where "these are not the numbers you typed" needs saying. It states what is owed and
 * who owes it; there is nothing to press.
 */
export const CorporateActionStatusTag: React.FC<{
    dataConfigId?: string | null;
    symbol?: string | null;
}> = ({ dataConfigId, symbol }) => {
    const [pending, setPending] = useState<PendingCorporateAction[] | null>(null);
    const [mode, setMode] = useState<'REVIEW' | 'AUTO' | null>(null);

    const load = useCallback(async () => {
        if (!dataConfigId) {
            setPending(null);
            return;
        }
        try {
            const state = await getConfigCorporateActions(dataConfigId);
            setPending(state.pending);
            setMode(state.mode);
        } catch {
            setPending(null);
        }
    }, [dataConfigId]);

    useEffect(() => {
        void load();
    }, [load]);

    const mine = symbol ? (pending ?? []).filter((p) => p.symbol === symbol) : [];
    if (mine.length === 0) return null;
    const blocked = mine.find((p) => p.status === 'BLOCKED');
    const first = blocked ?? mine[0];

    return (
        <Tooltip
            title={
                blocked
                    ? `${symbol}: adjustment blocked (${blocked.reason ?? 'needs operations'}). The bot will not start until it is resolved.`
                    : `${symbol}: corporate action adjustment pending for ex-date ${first.exDate}. The platform does it for every account holding the symbol${mode === 'AUTO' ? ' (automatically at 08:15 on the ex-date)' : ' once an admin confirms'}; the bot must be stopped.`
            }
        >
            <Tag color={blocked ? 'red' : 'gold'} style={{ fontSize: 11, marginInlineEnd: 0 }}>
                {blocked ? 'corp action: blocked' : `corp action: pending ${first.exDate}`}
            </Tag>
        </Tooltip>
    );
};

export default CorporateActionPanel;
